type Tone = 'ink' | 'warm' | 'inverse';

export type GlyphName =
  | 'sapling'
  | 'page'
  | 'arcs'
  | 'horizon'
  | 'spiral'
  | 'hook'
  | 'hand'
  | 'mirror'
  | 'frame'
  | 'notebook'
  | 'path'
  | 'prompt';

interface GlyphProps {
  name: GlyphName;
  size?: number;
  tone?: Tone;
  className?: string;
}

const TONE_INK: Record<Tone, { ink: string; accent: string }> = {
  ink: { ink: '#1F1B16', accent: '#1F7E68' },
  warm: { ink: '#3A332B', accent: '#B8552B' },
  inverse: { ink: '#FDFBF7', accent: '#7DD3B8' },
};

export default function Glyph({ name, size = 24, tone = 'ink', className = '' }: GlyphProps) {
  const { ink, accent } = TONE_INK[tone];
  const sw = Math.max(2, size * 0.085);

  const common = {
    width: size,
    height: size,
    viewBox: '0 0 36 36',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
    className,
  } as const;

  switch (name) {
    case 'sapling':
      return (
        <svg {...common}>
          <path
            d="M18 30.5 Q 17.6 24 18.2 16.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M18 18 Q 11.5 16.5 9.2 10.4 Q 14.8 11 18 16"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.2 21.6 Q 24.5 20.4 26.6 14.6 Q 21.8 15.4 18.2 19.6"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'page':
      return (
        <svg {...common}>
          <path
            d="M9.4 6.6 L23.2 6.4 Q 27.6 11 27.2 14.4 L27 28.6 Q 21 29.6 9.6 29.2 Z"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M22.6 6.4 Q 22.4 11.2 27.2 11.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13 17.4 Q 17 16.6 22.6 17.2"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M13 22.4 Q 16.6 21.8 20.2 22.2"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'arcs':
      return (
        <svg {...common}>
          <path
            d="M5.6 24.2 Q 12 9.4 19.4 22.6"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M16.6 24.2 Q 23.4 10.6 30.6 23.4"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'horizon':
      return (
        <svg {...common}>
          <path
            d="M18 6.4 Q 17.6 9.4 18 11.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M9.4 14.6 Q 18 11 26.8 15"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <circle cx="18" cy="20" r="3.4" stroke={ink} strokeWidth={sw} />
          <path
            d="M5.4 28.6 Q 18 26.4 30.6 28.8"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'spiral':
      return (
        <svg {...common}>
          <path
            d="M19.4 18 Q 19.4 14.4 16.4 14.4 Q 13 14.4 13 18.4 Q 13 23.6 18.6 23.6 Q 25.4 23.6 25.4 16.6 Q 25.4 8.4 16.4 8.4 Q 6.6 8.4 6.6 19.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <circle cx="28.4" cy="26.4" r={sw * 0.9} fill={accent} />
        </svg>
      );
    case 'hook':
      return (
        <svg {...common}>
          <path
            d="M11.4 8.6 Q 22.6 6.6 23.4 15.6 Q 23.6 22.4 17.6 22.6 Q 13.4 22.6 13.4 19.2"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <circle cx="17.6" cy="29" r={sw * 0.95} fill={accent} />
        </svg>
      );
    case 'hand':
      return (
        <svg {...common}>
          <path
            d="M9.4 18.6 Q 8.6 23 11.6 26.4 Q 16 30.4 22.6 28.6 Q 27.4 26.6 27.6 21.6 L27.6 14.4 Q 27.4 12.6 25.6 12.6 Q 23.8 12.6 23.6 14.4 L23.6 18.4 L23.6 9.4 Q 23.4 7.4 21.6 7.4 Q 19.6 7.4 19.6 9.4 L19.6 18.6 L19.6 8.4 Q 19.4 6.4 17.4 6.4 Q 15.4 6.4 15.4 8.6 L15.4 19.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15.4 19.4 Q 12.4 17 9.6 18.4"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'mirror':
      return (
        <svg {...common}>
          <path
            d="M11.4 7.6 Q 18 6.6 24.4 7.6 Q 24.6 13 24.4 17.4 Q 18 18.6 11.4 17.4 Q 11 12.4 11.4 7.6 Z"
            stroke={ink}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path
            d="M14.6 22.4 Q 17.6 24.4 21.2 22.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M18 22.4 L18 28.6"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'frame':
      return (
        <svg {...common}>
          <path
            d="M7.4 8.6 L20.6 8.4 Q 21 13.6 20.6 22.6 L7.6 22.4 Q 7 15.6 7.4 8.6 Z"
            stroke={ink}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path
            d="M16.4 15.6 Q 23.4 14.6 29.6 15.4"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M25.4 11.6 Q 29.4 14.6 29.6 15.4 Q 27.4 17.6 25.4 19.6"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'notebook':
      return (
        <svg {...common}>
          <path
            d="M9.4 7.4 Q 19 6.6 26.6 7.4 L26.4 28.6 Q 17.4 29.4 9.6 28.6 Z"
            stroke={ink}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path
            d="M9.4 12.4 Q 19 11.4 26.6 12.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M13.4 18 Q 19.4 17.4 23.4 17.8"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <path
            d="M13.4 22.4 Q 18 22 21.4 22.4"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'path':
      return (
        <svg {...common}>
          <path
            d="M7.4 27.6 Q 14 22.4 13.6 17.4 Q 13.2 12.4 19.4 11.6 Q 25.6 11 25.6 7.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
          />
          <circle cx="7.4" cy="27.6" r={sw * 0.9} fill={ink} />
          <circle cx="25.6" cy="7.4" r={sw} fill={accent} />
        </svg>
      );
    case 'prompt':
      return (
        <svg {...common}>
          <path
            d="M10.4 9.6 Q 13.4 13.4 18.4 18 Q 13.6 22.4 9.4 26.4"
            stroke={ink}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M21.4 26.6 Q 25 26.4 28 26.6"
            stroke={accent}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
  }
}

interface StampProps {
  glyph: GlyphName;
  size?: number;
  tone?: Tone;
  className?: string;
}

export function GlyphStamp({ glyph, size = 44, tone = 'ink', className = '' }: StampProps) {
  return (
    <span
      className={`relative inline-grid place-items-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 44 44"
        width={size}
        height={size}
        className="absolute inset-0"
        aria-hidden="true"
      >
        <path
          d="M6.4 9.6 Q 8 4.4 14.6 4.6 L31.6 4.4 Q 39.4 5.6 39.6 13.4 L39.4 31.4 Q 38.4 39.6 30.6 39.4 L11.6 39.6 Q 4.6 39 4.4 30.6 Z"
          fill="#F5EFE3"
          stroke="#E8DEC8"
          strokeWidth="1"
        />
      </svg>
      <span className="relative">
        <Glyph name={glyph} size={Math.round(size * 0.55)} tone={tone} />
      </span>
    </span>
  );
}
